import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { OAuthClientCard } from 'src/components/customer/oauth/OAuthClientCard'
import { OAuthClientSecretPanel } from 'src/components/customer/oauth/OAuthClientSecretPanel'
import { OAuthStatusBar } from 'src/components/customer/oauth/OAuthStatusBar'
import { RevokeOAuthTokensModal } from 'src/components/customer/oauth/RevokeOAuthTokensModal'
import {
  toCreatePayload,
  toUpdatePayload,
} from 'src/components/customer/oauth/oauth-client-form/OAuthClientForm.mapper'
import type { OAuthClientFormValues } from 'src/components/customer/oauth/oauth-client-form/OAuthClientForm.interface'
import {
  AdminCreateOAuthClient,
  AdminFetchOAuthClient,
  AdminRevokeOAuthTokens,
  AdminUpdateOAuthClient,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { CreatedOAuthClientResult, RevokeOAuthTokensResult } from 'src/interfaces/oauth'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import {
  getOAuthClient,
  getOAuthClientError,
  getOAuthClientLoaded,
  getOAuthClientPartnerId,
  getOAuthClientSaving,
  getOAuthTokensRevoking,
} from 'src/selectors/oauthClient'
import { isOAuthClientActive } from 'src/utils/oauthClient'

const CustomerOAuthPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const { isAuthenticated } = useAuthSession()
  const client = useSelector(getOAuthClient)
  const isLoaded = useSelector(getOAuthClientLoaded)
  const loadedPartnerId = useSelector(getOAuthClientPartnerId)
  const error = useSelector(getOAuthClientError)
  const isSaving = useSelector(getOAuthClientSaving)
  const isRevoking = useSelector(getOAuthTokensRevoking)
  // Create, save scopes and Enable/Disable are `oauth_clients_manage`; revoking tokens is its own
  // `oauth_tokens_revoke` (ENG-1207 part 2).
  const canManageClient = useHasPermission(InternalPermission.OauthClientsManage)
  const canRevokeTokens = useHasPermission(InternalPermission.OauthTokensRevoke)

  // Local, never Redux: the client secret comes back from POST exactly once, is never
  // refetchable, and anything dispatched is printed by redux-logger and captured by Sentry.
  const [createdClientSecret, setCreatedClientSecret] = useState<string | null>(null)
  const [isScopeFormDirty, setIsScopeFormDirty] = useState<boolean>(false)
  const [isConfirmingRevoke, setIsConfirmingRevoke] = useState<boolean>(false)

  const isReady = isLoaded && loadedPartnerId === customerId

  const fetchClient = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchOAuthClient.request({ partnerId: customerId }))
  }, [customerId, asyncDispatchWithErrorToast])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    fetchClient()
  }, [isAuthenticated, fetchClient])

  // Local UI state must not survive a customer switch. React Router reuses this component
  // instance when only `:customerId` changes, and the confirm modal's state lives here rather
  // than in the modal (which unmounts while `isReady` is false), so without this reset a
  // dialog opened for the previous customer reappears under the new one — with its confirm
  // wired to the new customerId.
  useEffect(() => {
    setIsConfirmingRevoke(false)
    setIsScopeFormDirty(false)
    // Satisfies the Retool app's "select row → clear oauth secrets" behaviour, which the
    // reducer used to handle before the secret moved out of the store.
    setCreatedClientSecret(null)
  }, [customerId])

  /**
   * The customer on screen right now, readable from inside an async callback.
   *
   * `customerId` captured in a mutation handler is the value from when the admin pressed the
   * button. If that mutation resolves after they have navigated to another customer, none of
   * its follow-up effects belong to the page any more — and one of them actively corrupts it:
   * the captured `fetchClient` would dispatch a fetch for the OLD partner, which the reducer
   * accepts, wiping the client of the customer now displayed. A stale completion could also
   * close the new customer's revoke dialog or toast a success over their page.
   *
   * Every post-await branch therefore compares its captured id against this ref and bails.
   */
  const currentCustomerIdRef = useRef<string | undefined>(customerId)

  useEffect(() => {
    currentCustomerIdRef.current = customerId
  }, [customerId])

  const handleSubmit = useCallback(
    async (values: OAuthClientFormValues) => {
      if (!customerId) {
        return
      }

      if (client === null) {
        const created = await asyncDispatchWithErrorToast<CreatedOAuthClientResult>(
          AdminCreateOAuthClient.request(toCreatePayload(customerId, values)),
        )

        if (currentCustomerIdRef.current !== customerId) {
          return
        }

        if (created) {
          // The secret arrives only as the resolved value of the create action, never as state.
          setCreatedClientSecret(created.clientSecret)
          snackbar?.enqueueSnackbar('API client created', { variant: 'success' })
        } else {
          // A 400 'OAuth client already exists' means another admin created one between our
          // fetch and this submit. Deliberately not retried as a PUT: that would apply our
          // scopes over their brand-new client, whose secret only they hold. Refetch instead,
          // which settles the page into manage mode showing what actually exists.
          fetchClient()
        }

        return
      }

      const updated = await asyncDispatchWithErrorToast(
        AdminUpdateOAuthClient.request(toUpdatePayload(customerId, values, client)),
      )

      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      if (updated) {
        snackbar?.enqueueSnackbar('Access scopes updated', { variant: 'success' })
      } else {
        // 404 'OAuth client not found' — the client went away out of band. Refetch so the page
        // falls back to create mode rather than offering saves that cannot land.
        fetchClient()
      }
    },
    [customerId, client, asyncDispatchWithErrorToast, snackbar, fetchClient],
  )

  const handleToggleStatus = useCallback(async () => {
    if (!customerId || client === null) {
      return
    }

    const shouldDeactivate = isOAuthClientActive(client)

    // Sends the *persisted* scopes, never the form's — the toggle changes status only. The
    // button is disabled while the form is dirty, so these cannot silently diverge.
    const updated = await asyncDispatchWithErrorToast(
      AdminUpdateOAuthClient.request({
        partnerId: customerId,
        scopes: client.scopes,
        deactivate: shouldDeactivate,
      }),
    )

    if (currentCustomerIdRef.current !== customerId) {
      return
    }

    if (updated) {
      snackbar?.enqueueSnackbar(shouldDeactivate ? 'API access disabled' : 'API access enabled', {
        variant: 'success',
      })
    } else {
      fetchClient()
    }
  }, [customerId, client, asyncDispatchWithErrorToast, snackbar, fetchClient])

  const handleOpenRevoke = useCallback(() => {
    setIsConfirmingRevoke(true)
  }, [])

  const handleCloseRevoke = useCallback(() => {
    setIsConfirmingRevoke(false)
  }, [])

  const handleConfirmRevoke = useCallback(async () => {
    if (!customerId) {
      return
    }

    const result = await asyncDispatchWithErrorToast<RevokeOAuthTokensResult>(
      AdminRevokeOAuthTokens.request({ partnerId: customerId }),
    )

    if (currentCustomerIdRef.current !== customerId) {
      return
    }

    if (result) {
      const label =
        result.revokedCount === 1 ? '1 token revoked' : `${result.revokedCount} tokens revoked`

      snackbar?.enqueueSnackbar(label, { variant: 'success' })
      setIsConfirmingRevoke(false)
    }
  }, [customerId, asyncDispatchWithErrorToast, snackbar])

  const handleDismissSecret = useCallback(() => {
    setCreatedClientSecret(null)
  }, [])

  if (!customerId) {
    return null
  }

  return (
    <div data-testid="admin-customer-oauth-page" css={tw`mx-auto w-full max-w-[720px] p-6`}>
      <Typography variant="h2" css={tw`mb-5`}>
        OAuth Client
      </Typography>

      {error && !isReady ? (
        <Typography variant="body2" css={tw`text-critical-500`}>
          {error}
        </Typography>
      ) : null}

      {!isReady && !error ? (
        <SectionCardSkeleton fields={3} dataTestId="admin-customer-oauth-loading" />
      ) : null}

      {isReady ? (
        <div css={tw`flex flex-col gap-6`}>
          {client ? (
            <OAuthStatusBar
              client={client}
              canToggleStatus={canManageClient}
              canRevoke={canRevokeTokens}
              isScopeFormDirty={isScopeFormDirty}
              isSaving={isSaving}
              isRevoking={isRevoking}
              onToggleStatus={handleToggleStatus}
              onOpenRevoke={handleOpenRevoke}
            />
          ) : null}

          {createdClientSecret ? (
            <OAuthClientSecretPanel
              clientSecret={createdClientSecret}
              onDismiss={handleDismissSecret}
            />
          ) : null}

          <OAuthClientCard
            client={client}
            customerId={customerId}
            isReadOnly={!canManageClient}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onDirtyChange={setIsScopeFormDirty}
          />

          <RevokeOAuthTokensModal
            isOpen={isConfirmingRevoke}
            isRevoking={isRevoking}
            onConfirm={handleConfirmRevoke}
            onClose={handleCloseRevoke}
          />
        </div>
      ) : null}
    </div>
  )
}

export const CustomerOAuthPage = memo(CustomerOAuthPageComponent)
